import React from "react";
import { Container, CssContainer } from "../../components/container";
import ManageDataTabs from "../../components/manage-data-tabs";
import { Loader } from "../../components/loaders";
import Error from "../../components/error";
import ServiceFactory from "../../service";
import account from "../../data/account";
import { manageDataLegacyHere } from "../../functions/tenant-features";
import { Card, Pill, Empty, Pending } from "../../components/analytics-kit";
import DataShell, { Btn, Notice } from "../manage-data/shell";

/*
 * Tracking — allowed websites (§290 / SITE_CHANNEL_DIMENSION.md §2).
 * Owner/admin surface managing which hosts count in Site Activity rollups.
 * Zero rows = allowAll (the opt-in default — every tenant's current state);
 * enforcement is build-time, so edits change tomorrow's rollups, never the
 * raw data or history.
 *
 * Rails (§293, live): GET /v1/activity/tracking/sites →
 * { allowAll, hosts:[{host, modifiedDate}] } · POST {host} (idempotent,
 * alreadyAllowed on repeat; accepts hostnames or app://bundle, ≤150 chars) ·
 * DELETE ?host= (query param — app:// values can't ride a path segment).
 * Observed traffic (and the >1-hosts nudge) reads GET /v1/activity/hosts/{range}.
 */

const svc = api => new (ServiceFactory.getType(api))();
const isAdmin = () => {
  const groups = ((account.get() || {}).groups || []).map(g => String(g).toLowerCase());
  return ["owner", "administrator", "root"].some(g => groups.includes(g));
};
const num = v => (Number(v) || 0).toLocaleString("en-US");
const day = d => d ? String(d).slice(0, 10) : "—";
export default function TrackingSitesView() {
  const [list, setList] = React.useState(null); // {allowAll, hosts[]}
  const [observed, setObserved] = React.useState(null); // hosts/{range} rows
  const [error, setError] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [note, setNote] = React.useState(null);
  const [draft, setDraft] = React.useState("");
  const [confirmRemove, setConfirmRemove] = React.useState(null);
  const [observedFailed, setObservedFailed] = React.useState(false);
  const legacy = manageDataLegacyHere();

  // The allowlist reloads after every add/remove; observed traffic is slow (about a minute)
  // and doesn't change with the list, so it loads once per visit.
  const load = React.useCallback(() => {
    svc("/tracking/sites").getAsync().then(d => setList({
      allowAll: !!d.allowAll,
      hosts: d.hosts || []
    })).catch(() => setError(true));
  }, []);
  React.useEffect(() => {
    load();
    let alive = true;
    // Observed traffic is decoration — a failure here never blocks the manager.
    svc("/hosts/this_month").getAsync().then(d => {
      if (alive) setObserved(d && d.hosts || []);
    }).catch(() => {
      if (alive) {
        setObserved([]);
        setObservedFailed(true);
      }
    });
    return () => {
      alive = false;
    };
  }, [load]);
  const msgOf = (e, fallback) => {
    try {
      const b = JSON.parse(e.message || "{}");
      if (b.error) return b.error;
    } catch (x) {/* not json */}
    return e && e.error || fallback;
  };
  const add = host => {
    const h = String(host || "").trim();
    if (!h) return;
    if (h.length > 150) {
      setNote(legacy ? "Hostnames are limited to 150 characters." : "That address is too long. Use 150 characters or fewer.");
      return;
    }
    setBusy(true);
    setNote(null);
    svc("/tracking/sites").postAsync({
      host: h
    }).then(r => {
      setDraft("");
      setNote(r && r.alreadyAllowed ? `${h} was already on the list.` : legacy ? `${h} added — it counts from tomorrow's rollups.` : `${h} added. Its visits count from tomorrow.`);
      load();
    }).catch(e => setNote(msgOf(e, legacy ? "Couldn't add that host — check the hostname and try again." : "That website couldn't be added. Check the address and try again."))).finally(() => setBusy(false));
  };
  const remove = host => {
    setBusy(true);
    setNote(null);
    setConfirmRemove(null);
    svc(`/tracking/sites?host=${encodeURIComponent(host)}`).deleteAsync().then(() => {
      setNote(legacy ? `${host} removed from the allowlist.` : `${host} removed from the list.`);
      load();
    }).catch(e => setNote(msgOf(e, legacy ? "Couldn't remove that host." : "That website couldn't be removed. Try again in a minute."))).finally(() => setBusy(false));
  };
  const SUB = "Tracking: which websites count in Site Activity; changes apply from tomorrow's rollups";
  if (!isAdmin()) {
    if (!legacy) return /*#__PURE__*/React.createElement(DataShell, {
      active: "/Data/Tracking",
      subtitle: SUB
    }, /*#__PURE__*/React.createElement(Empty, null, "Tracking settings are available to workspace owners and administrators."));
    return /*#__PURE__*/React.createElement("div", {
      className: "territory-view"
    }, /*#__PURE__*/React.createElement(ManageDataTabs, {
      active: "/Data/Tracking"
    }), /*#__PURE__*/React.createElement(CssContainer, {
      cls: "margin-squeeze"
    }, /*#__PURE__*/React.createElement("p", {
      className: "module-empty"
    }, "Tracking settings are available to workspace owners and administrators.")));
  }
  if (error) return legacy ? /*#__PURE__*/React.createElement("div", {
    className: "territory-view"
  }, /*#__PURE__*/React.createElement(ManageDataTabs, {
    active: "/Data/Tracking"
  }), /*#__PURE__*/React.createElement(Error, null)) : /*#__PURE__*/React.createElement(DataShell, {
    active: "/Data/Tracking",
    subtitle: SUB
  }, /*#__PURE__*/React.createElement(Error, null));
  if (!list) return legacy ? /*#__PURE__*/React.createElement("div", {
    className: "territory-view"
  }, /*#__PURE__*/React.createElement(ManageDataTabs, {
    active: "/Data/Tracking"
  }), /*#__PURE__*/React.createElement(Loader, null)) : /*#__PURE__*/React.createElement(DataShell, {
    active: "/Data/Tracking",
    subtitle: SUB
  }, /*#__PURE__*/React.createElement(Loader, null));
  const allowed = new Set(list.hosts.map(h => h.host));
  const extras = (observed || []).filter(h => !allowed.has(h.host));
  const showNudge = list.allowAll && observed && observed.length > 1;
  if (!legacy) {
    // Tracking on the kit (dataflow mockup "Tracking", 2026-10-09): one status line, the list of
    // websites to count, and where this month's visits came from. The observed-traffic call
    // takes about a minute, so that card shows a placeholder until it answers.
    const n = list.hosts.length;
    const totalViews = (observed || []).reduce((t, h) => t + (Number(h.views) || 0), 0);
    const statusSub = list.allowAll ? `${observed && observed.length > 1 ? `Your tracker saw visits on ${observed.length} websites this month. ` : ""}To count only some of them, add those websites below. Changes apply from tomorrow; past numbers stay as they are.` : "Visits to other websites aren't counted. Changes apply from tomorrow; past numbers stay as they are.";
    return /*#__PURE__*/React.createElement(DataShell, {
      active: "/Data/Tracking",
      cls: "df-view"
    }, note ? /*#__PURE__*/React.createElement(Notice, {
      onClose: () => setNote(null)
    }, /*#__PURE__*/React.createElement("span", {
      className: "grow"
    }, note)) : null, /*#__PURE__*/React.createElement("div", {
      className: "df-stack"
    }, /*#__PURE__*/React.createElement("section", {
      className: "df-summary"
    }, /*#__PURE__*/React.createElement("span", {
      className: "df-dot lg ok"
    }), /*#__PURE__*/React.createElement("div", {
      className: "df-summary-t"
    }, /*#__PURE__*/React.createElement("b", null, list.allowAll ? "Counting visits from every website your tracker is on" : `Counting visits from ${n} ${n === 1 ? "website" : "websites"}`), /*#__PURE__*/React.createElement("span", null, statusSub))), /*#__PURE__*/React.createElement("div", {
      className: "df-track"
    }, /*#__PURE__*/React.createElement("section", {
      className: "df-panel"
    }, /*#__PURE__*/React.createElement("div", {
      className: "df-panel-h"
    }, /*#__PURE__*/React.createElement("b", null, "Websites to count"), /*#__PURE__*/React.createElement("span", null, "Add each address exactly, with and without www if you use both.")), /*#__PURE__*/React.createElement("div", {
      className: "df-add"
    }, /*#__PURE__*/React.createElement("input", {
      type: "text",
      value: draft,
      placeholder: "example.com",
      "aria-label": "Website address",
      onChange: e => setDraft(e.target.value),
      onKeyDown: e => {
        if (e.key === "Enter") add(draft);
      }
    }), /*#__PURE__*/React.createElement(Btn, {
      disabled: busy || !draft.trim(),
      onClick: () => add(draft)
    }, "Add")), list.allowAll ? /*#__PURE__*/React.createElement("span", {
      className: "df-muted"
    }, "None added, so every website counts.") : /*#__PURE__*/React.createElement("ul", {
      className: "df-hosts"
    }, list.hosts.map(h => /*#__PURE__*/React.createElement("li", {
      key: h.host
    }, /*#__PURE__*/React.createElement("b", null, h.host), confirmRemove === h.host ? /*#__PURE__*/React.createElement("span", {
      className: "df-hosts-acts"
    }, /*#__PURE__*/React.createElement("span", {
      className: "df-muted"
    }, "Stop counting it?"), /*#__PURE__*/React.createElement("a", {
      className: "danger",
      onClick: () => !busy && remove(h.host)
    }, "Remove"), /*#__PURE__*/React.createElement("a", {
      onClick: () => setConfirmRemove(null)
    }, "Keep")) : /*#__PURE__*/React.createElement("span", {
      className: "df-hosts-acts"
    }, /*#__PURE__*/React.createElement("a", {
      onClick: () => !busy && setConfirmRemove(h.host)
    }, "Remove"))))), !list.allowAll && n === 1 ? /*#__PURE__*/React.createElement("span", {
      className: "df-muted"
    }, "Removing the last one goes back to counting every website.") : null), /*#__PURE__*/React.createElement("section", {
      className: "df-panel"
    }, /*#__PURE__*/React.createElement("div", {
      className: "df-panel-h"
    }, /*#__PURE__*/React.createElement("b", null, "Where visits came from this month")), observed === null ? /*#__PURE__*/React.createElement("div", {
      className: "df-loading"
    }, /*#__PURE__*/React.createElement(Pending, {
      h: 120
    }), /*#__PURE__*/React.createElement("span", {
      className: "df-muted"
    }, "Loading this month's websites, this can take a minute.")) : observedFailed ? /*#__PURE__*/React.createElement(Empty, null, "This month's websites couldn't be loaded right now. The list on the left still works; come back to this in a few minutes.") : !observed.length ? /*#__PURE__*/React.createElement(Empty, null, "No visits recorded yet this month.") : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: "df-sites"
    }, /*#__PURE__*/React.createElement("div", {
      className: "df-sites-row head"
    }, /*#__PURE__*/React.createElement("span", null, "Website"), /*#__PURE__*/React.createElement("span", null, "Share"), /*#__PURE__*/React.createElement("span", {
      className: "n"
    }, "Page views"), /*#__PURE__*/React.createElement("span", null)), observed.map(h => {
      const share = totalViews ? (Number(h.views) || 0) / totalViews : 0;
      const pct = share > 0 ? Math.max(1, Math.round(share * 100)) : 0;
      return /*#__PURE__*/React.createElement("div", {
        className: "df-sites-row",
        key: h.host
      }, /*#__PURE__*/React.createElement("b", {
        title: h.host
      }, h.host), /*#__PURE__*/React.createElement("span", {
        className: "df-bar",
        title: `${pct}% of page views`
      }, /*#__PURE__*/React.createElement("span", {
        style: {
          width: `${pct}%`
        }
      })), /*#__PURE__*/React.createElement("span", {
        className: "n"
      }, num(h.views)), /*#__PURE__*/React.createElement("span", {
        className: "act"
      }, allowed.has(h.host) ? /*#__PURE__*/React.createElement("span", {
        className: "df-muted"
      }, "Counted") : /*#__PURE__*/React.createElement("a", {
        onClick: () => !busy && add(h.host)
      }, list.allowAll ? "Count only this" : "Add to list")));
    })), /*#__PURE__*/React.createElement("div", {
      className: "df-foot"
    }, "A test site showing up here is a good reason to count only your live website."))))));
  }
  return /*#__PURE__*/React.createElement("div", {
    className: "territory-view"
  }, /*#__PURE__*/React.createElement(ManageDataTabs, {
    active: "/Data/Tracking"
  }), /*#__PURE__*/React.createElement(CssContainer, {
    cls: "grid-button space-between margin-squeeze align-end"
  }, /*#__PURE__*/React.createElement("div", {
    className: "territory-detail-head"
  }, /*#__PURE__*/React.createElement("div", {
    className: "heading-label"
  }, "Tracking \u2014 Allowed Websites"))), showNudge && /*#__PURE__*/React.createElement(CssContainer, {
    cls: "margin-squeeze"
  }, /*#__PURE__*/React.createElement("p", {
    className: "territory-hint",
    style: {
      marginTop: 0
    }
  }, "Traffic from ", observed.length, " sites this month \u2014 review which ones should count? Allow the ones that are really yours below; anything left off stops counting in tomorrow's rollups.")), note && /*#__PURE__*/React.createElement(CssContainer, {
    cls: "margin-squeeze"
  }, /*#__PURE__*/React.createElement("p", {
    className: "territory-hint",
    style: {
      marginTop: 0
    }
  }, note)), /*#__PURE__*/React.createElement(Container, {
    title: "Allowlist",
    css: "margin-squeeze"
  }, list.allowAll ? /*#__PURE__*/React.createElement("p", {
    className: "module-empty",
    style: {
      marginBottom: 12
    }
  }, "All traffic is accepted \u2014 every host your tracker reports gets counted. Add a hostname to switch Site Activity to an allowlist. List ", /*#__PURE__*/React.createElement("code", null, "www."), " and the bare domain separately (exact match, no wildcards).") : /*#__PURE__*/React.createElement("table", {
    className: "territory-table"
  }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Host"), /*#__PURE__*/React.createElement("th", {
    style: {
      width: 130
    }
  }, "Updated"), /*#__PURE__*/React.createElement("th", {
    style: {
      width: 120
    }
  }))), /*#__PURE__*/React.createElement("tbody", null, list.hosts.map(h => /*#__PURE__*/React.createElement("tr", {
    key: h.host
  }, /*#__PURE__*/React.createElement("td", null, h.host), /*#__PURE__*/React.createElement("td", null, day(h.modifiedDate)), /*#__PURE__*/React.createElement("td", {
    style: {
      textAlign: "right"
    }
  }, confirmRemove === h.host ? /*#__PURE__*/React.createElement("span", {
    style: {
      fontSize: 13
    }
  }, "Remove?", " ", /*#__PURE__*/React.createElement("button", {
    className: "k-button",
    disabled: busy,
    onClick: () => remove(h.host)
  }, "Yes"), " ", /*#__PURE__*/React.createElement("button", {
    className: "k-button",
    onClick: () => setConfirmRemove(null)
  }, "No")) : /*#__PURE__*/React.createElement("button", {
    className: "k-button",
    disabled: busy,
    onClick: () => setConfirmRemove(h.host)
  }, "Remove")))))), /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      gap: 8,
      alignItems: "center",
      marginTop: 12
    }
  }, /*#__PURE__*/React.createElement("input", {
    type: "text",
    value: draft,
    placeholder: "example.com or app://bundle.id",
    onChange: e => setDraft(e.target.value),
    onKeyDown: e => {
      if (e.key === "Enter") add(draft);
    },
    style: {
      padding: "6px 10px",
      minWidth: 260
    }
  }), /*#__PURE__*/React.createElement("button", {
    className: "k-button k-primary",
    disabled: busy || !draft.trim(),
    onClick: () => add(draft)
  }, "Add host")), /*#__PURE__*/React.createElement("p", {
    className: "territory-hint"
  }, "Changes apply from tomorrow's rollups \u2014 historical data is unchanged.")), observed && observed.length > 0 && /*#__PURE__*/React.createElement(Container, {
    title: "Observed traffic \u2014 this month",
    css: "margin-squeeze"
  }, /*#__PURE__*/React.createElement("table", {
    className: "territory-table"
  }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Host"), /*#__PURE__*/React.createElement("th", {
    className: "num"
  }, "Views"), /*#__PURE__*/React.createElement("th", {
    className: "num"
  }, "Sessions"), /*#__PURE__*/React.createElement("th", {
    className: "num"
  }, "Visitors"), /*#__PURE__*/React.createElement("th", {
    style: {
      width: 100
    }
  }))), /*#__PURE__*/React.createElement("tbody", null, observed.map(h => /*#__PURE__*/React.createElement("tr", {
    key: h.host
  }, /*#__PURE__*/React.createElement("td", null, h.host, allowed.has(h.host) && /*#__PURE__*/React.createElement("span", {
    className: "rep-state-chip",
    style: {
      marginLeft: 8
    }
  }, "allowed")), /*#__PURE__*/React.createElement("td", {
    className: "num",
    style: {
      textAlign: "right"
    }
  }, num(h.views)), /*#__PURE__*/React.createElement("td", {
    className: "num",
    style: {
      textAlign: "right"
    }
  }, num(h.sessions)), /*#__PURE__*/React.createElement("td", {
    className: "num",
    style: {
      textAlign: "right"
    }
  }, num(h.visitors)), /*#__PURE__*/React.createElement("td", {
    style: {
      textAlign: "right"
    }
  }, !allowed.has(h.host) && /*#__PURE__*/React.createElement("button", {
    className: "k-button",
    disabled: busy,
    onClick: () => add(h.host)
  }, "Allow")))))), extras.length === 0 && !list.allowAll && /*#__PURE__*/React.createElement("p", {
    className: "territory-hint"
  }, "Every observed host is on the allowlist.")));
}